import React, { useMemo } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import PropTypes from 'prop-types';

import {
  getAssistedClockInEnabled,
  getAssistedClockOutEnabled,
} from 'selectors/session';

import { ASSISTED_STATUSES } from 'features/timesheets/constants';
import AssistedClockOutIndicator from 'features/timesheets/TimesheetsPage/dailyReview/DailyReviewView/TableRow/AssistedClockOutIndicator';
import { TIME_FORMAT } from 'features/timesheets/TimesheetsPage/TimecardModal/constants';
import AssistedClockInNote from 'features/timesheets/TimesheetsPage/TimecardModal/ReadMode/TimecardSection/AssistedClockInNote';
import AssistedClockOutNote from 'features/timesheets/TimesheetsPage/TimecardModal/ReadMode/TimecardSection/AssistedClockOutNote';
import {
  ISSUE_COMPONENT_MAP,
  ISSUE_PRIORITIES,
} from 'features/timesheets/TimesheetsPage/TimecardModal/ReadMode/TimecardSection/Issues/constants';
import Section from 'features/timesheets/TimesheetsPage/TimecardModal/Section';
import { formattedScheduledTime } from 'features/timesheets/TimesheetsPage/TimecardModal/utils';
import { duration } from 'features/timesheets/util';

import { cxHelpers } from 'util/className';
import { moment, momentInZone } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import { LATE_DURATION, NO_HOURS } from '../../constants';

const { REJECTED, INITIATED } = ASSISTED_STATUSES;

const { cx } = cxHelpers('TimecardSection');

const getPriority = type => ISSUE_PRIORITIES[type] ?? ISSUE_PRIORITIES.default;

function renderIssue(
  addTimebreak,
  issue,
  onChangeShift,
  shift,
  key,
  setChangesMade
) {
  const IssueComponent = ISSUE_COMPONENT_MAP[issue.get('type')];

  if (IssueComponent === undefined) {
    return null;
  }

  return (
    <Box mt={10}>
      <IssueComponent
        key={key}
        issue={issue}
        addTimebreak={addTimebreak}
        onChangeShift={onChangeShift}
        shift={shift}
        setChangesMade={setChangesMade}
      />
    </Box>
  );
}

const TimecardSection = ({
  addTimebreak,
  canManageTimecards,
  onChangeShift,
  shift,
  setChangesMade,
}) => {
  const assistedClockOutEnabled = useSelector(getAssistedClockOutEnabled);
  const assistedClockInEnabled = useSelector(getAssistedClockInEnabled);
  const assistedClockOut = shift.get('assisted_clock_out');
  const assistedClockIn = shift.get('assisted_clock_in');

  function actualDuration(startTime, endTime, isLate) {
    if (isLate) {
      return LATE_DURATION;
    }

    if (startTime && endTime && startTime.isValid() && endTime.isValid()) {
      return duration(endTime.diff(startTime, 'minutes'));
    }

    return <Text>{NO_HOURS}</Text>;
  }

  const momentStartAt = useMemo(
    () =>
      shift.get('actual_start_at')
        ? moment(shift.get('actual_start_at'))
        : null,
    [shift]
  );
  const momentEndAt = useMemo(
    () =>
      shift.get('actual_end_at') ? moment(shift.get('actual_end_at')) : null,
    [shift]
  );
  const momentScheduledStartAt = useMemo(
    () => moment(shift.get('scheduled_start_at')),
    [shift]
  );
  const momentScheduledEndAt = useMemo(
    () => moment(shift.get('scheduled_end_at')),
    [shift]
  );

  const isLateShift = shift.get('status') === 'late';
  const isInProgress =
    shift.get('status') === 'in_progress' || shift.get('status') === 'on_break';

  const isMultiDay =
    momentStartAt && momentEndAt && momentEndAt.date() !== momentStartAt.date();

  const currentIssues = useMemo(() => {
    const issues = shift.get('issues');
    if (!issues) return null;

    const filteredIssues = issues.filter(
      issue => !issue.get('resolved') || issue.get('type') !== 'auto_clock_out'
    );

    return filteredIssues.sort((a, b) => {
      const aType = a.get('type');
      const bType = b.get('type');
      return getPriority(aType) - getPriority(bType);
    });
  }, [shift]);

  return (
    <Box className={cx()}>
      <Section
        bottomBorder
        bottomPadding
        collapsedContent={
          <Box>
            <Box mt={4}>
              <Box inlineBlock>
                <Text
                  variant="bodyBold"
                  color="mono900"
                  navy={!isLateShift && !isInProgress}
                  red={isLateShift}
                  green={isInProgress}
                >
                  {actualDuration(
                    momentStartAt,
                    momentEndAt || (isInProgress ? momentInZone() : null),
                    isLateShift
                  )}
                </Text>
              </Box>
              {!isLateShift && (
                <Box ml={6} inlineBlock>
                  <Box row gap={2}>
                    <Text variant="body" color="mono900">
                      •{' '}
                      {momentStartAt ? momentStartAt.format(TIME_FORMAT) : '--'}{' '}
                      -
                    </Text>
                    <Text variant="body" color="mono900">
                      {momentEndAt ? momentEndAt.format(TIME_FORMAT) : '--'}
                    </Text>
                    {isMultiDay && (
                      <Text variant="body" color="mono900">
                        (+1)
                      </Text>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
            {shift.get('scheduled') && (
              <Box mt={4}>
                <Text
                  variant="captions"
                  color="mono700"
                  i18n="timecard_modal_v3.timecard_section.scheduled"
                  i18nProps={{
                    time: formattedScheduledTime(
                      shift.get('scheduled'),
                      momentScheduledStartAt,
                      momentScheduledEndAt
                    ),
                  }}
                />
              </Box>
            )}
            {canManageTimecards &&
              (currentIssues || [])
                .filter(issue =>
                  Object.keys(ISSUE_COMPONENT_MAP).includes(issue.get('type'))
                )
                .map((issue, key) =>
                  renderIssue(
                    addTimebreak,
                    issue,
                    onChangeShift,
                    shift,
                    key,
                    setChangesMade
                  )
                )}
            {assistedClockInEnabled &&
              assistedClockIn &&
              assistedClockIn.get('status') !== REJECTED &&
              assistedClockIn.get('comment') && (
                <Box mt={8}>
                  <AssistedClockInNote
                    submittedAt={assistedClockIn.get('submitted_at')}
                    comment={assistedClockIn.get('comment')}
                  />
                </Box>
              )}
            {assistedClockOutEnabled &&
              assistedClockOut &&
              assistedClockOut.get('status') !== REJECTED &&
              assistedClockOut.get('comment') && (
                <Box mt={8}>
                  <AssistedClockOutNote
                    submittedAt={assistedClockOut.get('submitted_at')}
                    comment={assistedClockOut.get('comment')}
                  />
                </Box>
              )}
            {assistedClockOutEnabled &&
              assistedClockOut &&
              assistedClockOut.get('status') !== REJECTED &&
              assistedClockOut.get('status') === INITIATED && (
                <Box mt={8}>
                  <AssistedClockOutIndicator
                    status={assistedClockOut.get('status')}
                    pastDue={assistedClockOut.get('past_due')}
                  />
                </Box>
              )}
          </Box>
        }
        iconName="TimeClock"
        title={toI18n('timecard_modal_v3.timecard_section.worked')}
      />
    </Box>
  );
};

TimecardSection.propTypes = {
  addTimebreak: PropTypes.func.isRequired,
  canManageTimecards: PropTypes.bool.isRequired,
  onChangeShift: PropTypes.func.isRequired,
  shift: ImmutablePropTypes.map.isRequired,
  setChangesMade: PropTypes.func.isRequired,
};

export default TimecardSection;
